/**
 * Utilities for homogenizing tree filter selections to a single granularity level.
 *
 * Some APIs (like composition tooltips) require all selections to be at the same
 * granularity level. These utilities expand selections from coarser levels to the
 * most granular level present in the filter.
 */

import {
  SelectionCategories,
  type SelectionList,
  type ValidValueTypes,
} from '@revelio/iso-utility';

import type { AllSelectionListsWithSkillTaxonomyKey } from '../../../../shared/selection-lists/types';
import type { TreeFilter, TreeSelection } from '../types/filters';

/** Maximum role IDs to prevent excessive data in tooltip requests */
const MAX_ROLE_FILTER_IDS = 3000;

/** Maximum RICS IDs to prevent excessive data in tooltip requests */
const MAX_RICS_FILTER_IDS = 50;

/** Role level hierarchy from coarsest to finest */
type RoleLevel = 'role_k7' | 'role_k150' | 'role_k1500' | 'role_k15000';

/** RICS level hierarchy from coarsest to finest */
type RicsLevel = 'rics_k10' | 'rics_k50' | 'rics_k400';

/** Role hierarchy configuration - defines the expansion path */
const ROLE_HIERARCHY: Record<
  RoleLevel,
  { next: RoleLevel | null; selectionCategory: SelectionCategories }
> = {
  role_k7: {
    next: 'role_k150',
    selectionCategory: SelectionCategories.JOB_CATEGORY,
  },
  role_k150: {
    next: 'role_k1500',
    selectionCategory: SelectionCategories.ROLE_K150,
  },
  role_k1500: {
    next: 'role_k15000',
    selectionCategory: SelectionCategories.ROLE_K1500,
  },
  role_k15000: {
    next: null,
    selectionCategory: SelectionCategories.ROLE_K15000,
  },
};

/** Role level priority from most to least granular */
const ROLE_LEVEL_PRIORITY: RoleLevel[] = [
  'role_k15000',
  'role_k1500',
  'role_k150',
  'role_k7',
];

/** RICS level priority from most to least granular */
const RICS_LEVEL_PRIORITY: RicsLevel[] = ['rics_k400', 'rics_k50', 'rics_k10'];

/**
 * Check if a levelId is a role level
 */
const isRoleLevel = (levelId: string): levelId is RoleLevel =>
  ROLE_LEVEL_PRIORITY.includes(levelId as RoleLevel);

/**
 * Check if a levelId is a RICS level
 */
const isRicsLevel = (levelId: string): levelId is RicsLevel =>
  RICS_LEVEL_PRIORITY.includes(levelId as RicsLevel);

/**
 * Extract the numeric ID from a tree selection ID string.
 * Tree selection IDs are typically formatted as "prefix_id" or just "id".
 */
const extractNumericId = (selectionId: string): string => {
  const parts = selectionId.split('_');
  return parts[parts.length - 1] ?? selectionId;
};

/**
 * Expand role IDs from a source level to a target level.
 * Uses selection lists to find children of parent nodes.
 */
const expandRoleIdsToLevel = (
  ids: string[],
  fromLevel: RoleLevel,
  toLevel: RoleLevel,
  selectionLists: SelectionList<ValidValueTypes>[],
  cache: Map<string, string[]>
): string[] => {
  if (fromLevel === toLevel || ids.length === 0) {
    return ids;
  }

  const cacheKey = `${fromLevel}->${toLevel}-${ids.join(',')}`;
  const cached = cache.get(cacheKey);
  if (cached) {
    return cached;
  }

  let currentLevel: RoleLevel = fromLevel;
  let currentIds: string[] = [...ids];

  // Iteratively walk down the hierarchy until we reach the target level
  while (currentLevel !== toLevel && currentIds.length > 0) {
    const config = ROLE_HIERARCHY[currentLevel];
    const nextLevel = config.next;
    if (!nextLevel) break;

    const childListCategory = ROLE_HIERARCHY[nextLevel].selectionCategory;
    const childList = selectionLists.find((l) => l.id === childListCategory);
    if (!childList) {
      currentIds = [];
      break;
    }

    const currentIdsSet = new Set(currentIds);
    const parentFieldName = currentLevel; // e.g., "role_k7" - the field name in child items

    type ChildItem = { id: string | number; [key: string]: unknown };

    const nextIds = (childList.value as ChildItem[])
      .filter((item) => {
        const parentVal = (item as Record<string, unknown>)[parentFieldName];
        const parentValStr = Array.isArray(parentVal)
          ? String(parentVal[0])
          : String(parentVal);
        return currentIdsSet.has(parentValStr);
      })
      .map((item) => String(item.id));

    currentIds = nextIds;
    currentLevel = nextLevel;
  }

  cache.set(cacheKey, currentIds);
  return currentIds;
};

/**
 * Homogenize role selections to the most granular level present.
 *
 * When a TreeFilter contains role selections at multiple levels (e.g., both role_k7
 * and role_k1500), this function expands all selections to the most granular level
 * present (role_k1500 in this case).
 *
 * @param filter - The TreeFilter containing role selections
 * @param selectionLists - Selection lists for looking up child nodes
 * @returns Homogenized selections all at the same (most granular) level
 */
export const homogenizeRoleSelections = (
  filter: TreeFilter,
  selectionLists: SelectionList<ValidValueTypes>[]
): TreeSelection[] => {
  const roleSelections = filter.selections.filter((s) =>
    isRoleLevel(s.levelId)
  );

  if (roleSelections.length === 0) {
    return filter.selections;
  }

  // Group selections by level
  const selectionsByLevel = roleSelections.reduce<
    Record<RoleLevel, TreeSelection[]>
  >(
    (acc, selection) => {
      const level = selection.levelId as RoleLevel;
      const existing = acc[level] ?? [];
      return { ...acc, [level]: [...existing, selection] };
    },
    {} as Record<RoleLevel, TreeSelection[]>
  );

  // Find the most granular level with selections
  const targetLevel = ROLE_LEVEL_PRIORITY.find(
    (level) => (selectionsByLevel[level]?.length ?? 0) > 0
  );

  if (!targetLevel) {
    return filter.selections;
  }

  // Get non-role selections to preserve
  const nonRoleSelections = filter.selections.filter(
    (s) => !isRoleLevel(s.levelId)
  );

  // Start with selections already at the target level
  const homogenizedIds: string[] = (selectionsByLevel[targetLevel] ?? []).map(
    (s) => extractNumericId(s.id)
  );

  // Expand coarser levels to the target level
  const expansionCache = new Map<string, string[]>();

  for (const level of ROLE_LEVEL_PRIORITY) {
    if (level === targetLevel) continue;

    const selectionsAtLevel = selectionsByLevel[level] ?? [];
    if (selectionsAtLevel.length === 0) continue;

    const idsAtLevel = selectionsAtLevel.map((s) => extractNumericId(s.id));
    const expandedIds = expandRoleIdsToLevel(
      idsAtLevel,
      level,
      targetLevel,
      selectionLists,
      expansionCache
    );

    homogenizedIds.push(...expandedIds);
  }

  // Deduplicate, limit, and resolve names
  const uniqueIds = Array.from(new Set(homogenizedIds))
    .filter(Boolean)
    .slice(0, MAX_ROLE_FILTER_IDS);

  const targetListCategory = ROLE_HIERARCHY[targetLevel].selectionCategory;
  const targetList = selectionLists.find((l) => l.id === targetListCategory);

  const homogenizedSelections: TreeSelection[] = uniqueIds.map((id) => {
    type ListItem = { id: string | number; shortName?: string; name?: string };
    const item = (targetList?.value as ListItem[] | undefined)?.find(
      (v) => String(v.id) === id
    );
    return {
      id,
      name: item?.shortName ?? item?.name ?? id,
      levelId: targetLevel as AllSelectionListsWithSkillTaxonomyKey,
    };
  });

  return [...nonRoleSelections, ...homogenizedSelections];
};

/**
 * Expand RICS K10 IDs to K50 IDs using selection lists.
 */
const expandRicsK10ToK50 = (
  ids: string[],
  selectionLists: SelectionList<ValidValueTypes>[]
): string[] => {
  if (ids.length === 0) return [];

  const ricsK50List = selectionLists.find(
    (list) => list.id === SelectionCategories.RICS_K50
  );
  if (!ricsK50List) return [];

  type RicsK50Item = { id: string | number; rics_k10: string | number };
  const idsSet = new Set(ids);

  return (ricsK50List.value as RicsK50Item[])
    .filter((item) => idsSet.has(String(item.rics_k10)))
    .map((item) => String(item.id));
};

/**
 * Expand RICS K50 IDs to K400 IDs using selection lists.
 */
const expandRicsK50ToK400 = (
  ids: string[],
  selectionLists: SelectionList<ValidValueTypes>[]
): string[] => {
  if (ids.length === 0) return [];

  const ricsK400List = selectionLists.find(
    (list) => list.id === SelectionCategories.RICS_K400
  );
  if (!ricsK400List) return [];

  type RicsK400Item = { id: string | number; rics_k50?: string | number };
  const idsSet = new Set(ids);

  return (ricsK400List.value as RicsK400Item[])
    .filter((item) => item.rics_k50 && idsSet.has(String(item.rics_k50)))
    .map((item) => String(item.id));
};

/**
 * Homogenize RICS selections to the most granular level present.
 *
 * When a TreeFilter contains RICS selections at multiple levels (e.g., both rics_k10
 * and rics_k400), this function expands all selections to the most granular level
 * present (rics_k400 in this case).
 *
 * @param filter - The TreeFilter containing RICS selections (typically COMPANY filter)
 * @param selectionLists - Selection lists for looking up child nodes
 * @returns Homogenized selections all at the same (most granular) level, plus non-RICS selections
 */
export const homogenizeRicsSelections = (
  filter: TreeFilter,
  selectionLists: SelectionList<ValidValueTypes>[]
): TreeSelection[] => {
  const ricsSelections = filter.selections.filter((s) =>
    isRicsLevel(s.levelId)
  );

  if (ricsSelections.length === 0) {
    return filter.selections;
  }

  // Group selections by level
  const selectionsByLevel = ricsSelections.reduce<
    Record<RicsLevel, TreeSelection[]>
  >(
    (acc, selection) => {
      const level = selection.levelId as RicsLevel;
      const existing = acc[level] ?? [];
      return { ...acc, [level]: [...existing, selection] };
    },
    {} as Record<RicsLevel, TreeSelection[]>
  );

  // Find the most granular level with selections
  const targetLevel = RICS_LEVEL_PRIORITY.find(
    (level) => (selectionsByLevel[level]?.length ?? 0) > 0
  );

  if (!targetLevel) {
    return filter.selections;
  }

  // Get non-RICS selections to preserve (company selections)
  const nonRicsSelections = filter.selections.filter(
    (s) => !isRicsLevel(s.levelId)
  );

  // Extract IDs by level
  const k10Ids = (selectionsByLevel['rics_k10'] ?? []).map((s) =>
    extractNumericId(s.id)
  );
  const k50Ids = (selectionsByLevel['rics_k50'] ?? []).map((s) =>
    extractNumericId(s.id)
  );
  const k400Ids = (selectionsByLevel['rics_k400'] ?? []).map((s) =>
    extractNumericId(s.id)
  );

  // Expand to target level
  const homogenizedIds: string[] = (() => {
    if (targetLevel === 'rics_k400') {
      const fromK50 = [
        ...k50Ids,
        ...expandRicsK10ToK50(k10Ids, selectionLists),
      ];
      return [...k400Ids, ...expandRicsK50ToK400(fromK50, selectionLists)];
    }
    if (targetLevel === 'rics_k50') {
      return [...k50Ids, ...expandRicsK10ToK50(k10Ids, selectionLists)];
    }
    return k10Ids;
  })();

  // Deduplicate and limit
  const uniqueIds = Array.from(new Set(homogenizedIds))
    .filter(Boolean)
    .slice(0, MAX_RICS_FILTER_IDS);

  // Resolve names from selection list
  const targetListCategory = (() => {
    switch (targetLevel) {
      case 'rics_k400':
        return SelectionCategories.RICS_K400;
      case 'rics_k50':
        return SelectionCategories.RICS_K50;
      case 'rics_k10':
        return SelectionCategories.RICS_K10;
    }
  })();

  const targetList = selectionLists.find((l) => l.id === targetListCategory);

  const homogenizedSelections: TreeSelection[] = uniqueIds.map((id) => {
    type ListItem = { id: string | number; shortName?: string; name?: string };
    const item = (targetList?.value as ListItem[] | undefined)?.find(
      (v) => String(v.id) === id
    );
    return {
      id,
      name: item?.shortName ?? item?.name ?? id,
      levelId: targetLevel as AllSelectionListsWithSkillTaxonomyKey,
    };
  });

  return [...nonRicsSelections, ...homogenizedSelections];
};
